/**
 * AssetResolver.ts
 * Resolves texture and model references for BBD export.
 * Walks the dungeon grid, calls TextureCatalog for each cell/wall,
 * and produces the resolvedAssets manifest + per-cell texture IDs.
 *
 * This runs at export time so World receives fully-resolved asset references
 * without needing DM's internal TextureCatalog logic.
 */

import type {
    OctileDungeon, OctileCell, SquareConnector, GridCell,
    ResolvedAssets, ResolvedTextureRef, ResolvedModelRef, CellTheme
} from '../types/OctileTypes.ts'
import { getFloorTexturePath, getWallTexturePath } from '../grid/TextureCatalog.ts'
import { getCellType } from '../grid/GridAddressing.ts'

// ─── Content ID → GLB Path Mapping ────────────────────────────

const CONTENT_MODEL_MAP: Record<string, { path: string; category: string }> = {
    // Loot
    barrel:         { path: 'models/barrels/barrel_001.glb', category: 'barrels' },
    chest_wooden:   { path: 'models/chests/chest_wood_plain.glb', category: 'chests' },
    chest_ornate:   { path: 'models/chests/chest_ornate.glb', category: 'chests' },
    pedestal:       { path: 'models/altars/throne_crimson.glb', category: 'altars' },  // placeholder until pedestal GLB
    // Props
    torch:          { path: 'models/lighting/torch_sconce.glb', category: 'lighting' },
    pillar:         { path: 'models/lighting/brazier_dragon.glb', category: 'lighting' },  // placeholder until pillar GLB
    altar:          { path: 'models/altars/altar_dragon.glb', category: 'altars' },
    cage:           { path: 'models/gates/gate_crimson_sigil_001.glb', category: 'gates' },  // placeholder until cage GLB
}

// ─── Base URL Configuration ───────────────────────────────────

const DEFAULT_BASE_URL = 'https://poqpoq.com/dungeon-master'

// ─── Resolver ─────────────────────────────────────────────────

export interface ResolvedCell {
    r: number
    c: number
    floorTextureId: string | null
    wallTextureIds: (string | null)[]
}

export interface ResolveResult {
    assets: ResolvedAssets
    cellTextureMap: ResolvedCell[]
}

/**
 * Resolve all asset references for a dungeon.
 * Returns the resolvedAssets manifest and per-cell texture IDs.
 */
export function resolveDungeonAssets(
    dungeon: OctileDungeon,
    baseUrl: string = DEFAULT_BASE_URL
): ResolveResult {
    const textureSet = new Map<string, ResolvedTextureRef>()
    const modelSet = new Map<string, ResolvedModelRef>()
    const allCellTextures: ResolvedCell[] = []

    for (const layer of dungeon.layers) {
        const grid = layer.grid

        for (let row = 0; row < grid.length; row++) {
            for (let col = 0; col < grid[row].length; col++) {
                const cell = grid[row][col]
                if (!cell?.active) continue

                const kind = getCellType(row, col)

                if (kind === 'octagon' && cell.kind === 'octagon') {
                    const resolved = resolveOctagonTextures(cell, grid, textureSet)
                    allCellTextures.push(resolved)
                    collectContentModels(cell, modelSet)
                } else if (kind === 'connector' && cell.kind === 'connector') {
                    const resolved = resolveConnectorTextures(cell, grid, textureSet)
                    allCellTextures.push(resolved)
                }
            }
        }
    }

    return {
        assets: {
            baseUrl,
            textures: Array.from(textureSet.values()),
            models: Array.from(modelSet.values()),
        },
        cellTextureMap: allCellTextures,
    }
}

// ─── Texture ID Generation ────────────────────────────────────

/** Convert a texture path to a stable ID: 'textures/floors/cave_natural_001.png' → 'floor_cave_natural_001' */
function pathToTextureId(path: string): string {
    return path
        .replace('textures/floors/', 'floor_')
        .replace('textures/walls/', 'wall_')
        .replace('.png', '')
}

/** Register a texture path and return its ID */
function registerTexture(
    path: string,
    textureSet: Map<string, ResolvedTextureRef>
): string {
    const id = pathToTextureId(path)
    if (!textureSet.has(id)) {
        textureSet.set(id, { id, path })
    }
    return id
}

// ─── Octagon Texture Resolution ───────────────────────────────

function resolveOctagonTextures(
    cell: OctileCell,
    _grid: GridCell[][],
    textureSet: Map<string, ResolvedTextureRef>
): ResolvedCell {
    // Floor texture
    const floorPath = getFloorTexturePath(cell.type, cell.theme, cell.row, cell.col)
    const floorTextureId = registerTexture(floorPath, textureSet)

    // 8 wall textures
    const wallTextureIds: (string | null)[] = []
    for (let i = 0; i < 8; i++) {
        const wallPath = getWallTexturePath(cell.walls[i], cell.type, cell.theme, cell.row, cell.col, i)
        wallTextureIds.push(wallPath ? registerTexture(wallPath, textureSet) : null)
    }

    return {
        r: cell.row,
        c: cell.col,
        floorTextureId,
        wallTextureIds,
    }
}

// ─── Connector Texture Resolution ─────────────────────────────

function resolveConnectorTextures(
    cell: SquareConnector,
    grid: GridCell[][],
    textureSet: Map<string, ResolvedTextureRef>
): ResolvedCell {
    // Connector theme: first active diagonal octagon neighbor
    const connTheme = getConnectorTheme(cell, grid)

    // Floor texture (connectors always use 'passage' type)
    const floorPath = getFloorTexturePath('passage', connTheme, cell.row, cell.col)
    const floorTextureId = registerTexture(floorPath, textureSet)

    // 4 wall textures
    const wallTextureIds: (string | null)[] = []
    for (let i = 0; i < 4; i++) {
        const wallPath = getWallTexturePath(cell.walls[i], 'empty', connTheme, cell.row, cell.col, i)
        wallTextureIds.push(wallPath ? registerTexture(wallPath, textureSet) : null)
    }

    return {
        r: cell.row,
        c: cell.col,
        floorTextureId,
        wallTextureIds,
    }
}

/** Get the theme of the first active diagonal octagon neighbor */
function getConnectorTheme(cell: SquareConnector, grid: GridCell[][]): CellTheme {
    const offsets = [[-1, -1], [-1, 1], [1, -1], [1, 1]]
    for (const [dr, dc] of offsets) {
        const neighbor = grid[cell.row + dr]?.[cell.col + dc]
        if (neighbor?.active && neighbor.kind === 'octagon') {
            return (neighbor as OctileCell).theme
        }
    }
    return 'natural_cave'
}

// ─── Content Model Collection ─────────────────────────────────

function collectContentModels(
    cell: OctileCell,
    modelSet: Map<string, ResolvedModelRef>
): void {
    const allContentIds = [
        ...cell.loot,
        ...cell.decorations,
        // spawns and traps are World's models, not DM's — skip
    ]

    for (const contentId of allContentIds) {
        const mapping = CONTENT_MODEL_MAP[contentId]
        if (mapping && !modelSet.has(contentId)) {
            modelSet.set(contentId, {
                id: contentId,
                path: mapping.path,
                category: mapping.category,
            })
        }
    }
}
